import React, { useEffect, useState } from "react";
import dayjs from 'dayjs';

import { Box, Chip, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Components
import MultiLineChart from '../../components/MultiLineChart';

// Apis
import { getTokenSnapshot } from '../../api/token-snapshot';
import { getAllNetworks } from '../../api/network';

const InterestCharts = ({ symbol }) => {

    // read from and to from url query params, they are timestamps
    const urlParams = new URLSearchParams(window.location.search);
    const from = urlParams.get('from');
    const to = urlParams.get('to');

    // convert to dayjs object from timestamp
    const from_date = from ? dayjs.unix(from) : dayjs().subtract(1, 'month');
    const to_date = to ? dayjs.unix(to) : dayjs();

    const [snapshotHistory, setSnapshotHistory] = useState([]);
    const [networks, setNetworks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [chartOptions, setChartOptions] = useState({
        from: from_date,
        to: to_date,
        networks: [],
    });

    const handleChange = (key, value) => {
    setChartOptions((prev) => ({
        ...prev,
        [key]: value,
    }));
    }

    const handleDateChange = (type, date) => {
    // update url query params
    const timestamp = date.unix();
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set(type, timestamp);
    window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`);
    handleChange(type, date);
    };

    const fetchData = () => {
    setIsLoading(true);
    getTokenSnapshot(symbol, chartOptions).then((data) => {
        setSnapshotHistory(data);
        setIsLoading(false);
    });
    };

    useEffect(() => {
    fetchData();
    }, [symbol, chartOptions]);

    useEffect(() => {
    getAllNetworks().then((data) => {
        setNetworks(data);
    });
    }, []);

    const liquidityRateData = snapshotHistory.map((snapshot) => ({
        x: snapshot.last_update_timestamp * 1000,
        y: snapshot.liquidity_rate / (10 ** 25),
        prefix: 'Liquidity Rate',
        suffix: '%',
      }));
      const variableBorrowRateData = snapshotHistory.map((snapshot) => ({
        x: snapshot.last_update_timestamp * 1000,
        y: snapshot.variable_borrow_rate / (10 ** 25),
        prefix: 'Variable Borrow Rate',
        suffix: '%',
      }));
      const stableBorrowRateData = snapshotHistory.map((snapshot) => ({
        x: snapshot.last_update_timestamp * 1000,
        y: snapshot.stable_borrow_rate / (10 ** 25),
        prefix: 'Stable Borrow Rate',
        suffix: '%',
      }));
    
      const lines = [] 
      lines.push({
        data: liquidityRateData,
        key: 'liquidity_rate',
        label: 'Liquidity Rate',
      })
      lines.push({
        data: variableBorrowRateData,
        key: 'variable_borrow_rate',
        label: 'Variable Borrow Rate',
      })
      lines.push({
        data: stableBorrowRateData,
        key: 'stable_borrow_rate',
        label: 'Stable Borrow Rate',
      })


    return (
        <Box>
            <Box sx={{
            width: '100%', 
            display: 'flex', 
            flexDirection: 'row', 
            justifyContent: 'center', 
            alignItems: 'center', 
            mb: 2, 
            gap: 2, 
            backgroundColor: 'var(--background-color)',
            pt: 2,
            pb: 1,
            }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                sx={{marginTop: 2}}
                label="From Date"
                value={chartOptions.from}
                onChange={(newValue) => handleDateChange('from', newValue)}
                renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                />
                <DatePicker
                sx={{marginTop: 3}}
                label="To Date"
                value={chartOptions.to}
                onChange={(newValue) => handleDateChange('to', newValue)}
                renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                />
            </LocalizationProvider>
            </Box>

        <MultiLineChart
        lines={lines}
        isLoading={isLoading}
        filter={<Box sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 4,
          marginTop: 2,
        }}>
          <FormControl fullWidth margin="normal">
            <InputLabel labelId="network-select-label">Networks</InputLabel>
            <Select
            label="Networks"
            labelId='network-select-label'
            value={chartOptions.networks}
            multiple
            onChange={(e) => handleChange('networks', e.target.value)}
            renderValue={(selected) => (
              <Box sx={{ gap: 0.5, flexWrap: 'wrap', display: 'flex', overflowY: 'hidden' }}>
                  {selected.map((value) => (
                      <Chip key={value} label={value} />
                  ))}
              </Box>
          )}
            >
              {
                networks.map((network) => (
                  <MenuItem key={network} value={network}>{network}</MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </Box>}
      />
    </Box>);
};

export default InterestCharts;
